import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FeaturedRail } from '@Components/AbwaabPlus'
import { useGetFeaturedStandaloneCoursesQuery } from '@Features/StandaloneCourse/StandaloneCourseApi/StandaloneCourseApi'
import StandaloneCourseCard from '@Features/StandaloneCourse/Components/StandaloneCourseCard'
import usePlatformFlags from '@Hooks/usePlatformFlags'

/**
 * The admin-curated featured-courses carousel — shared by the guest landing
 * and the student home (the featured endpoint is public). Self-hiding: renders
 * nothing when the platform flag is off or nothing is featured, so each
 * landing can mount it unconditionally.
 */
const FeaturedCoursesCarousel: React.FC = () => {
	const [t] = useTranslation('standalone_course')
	const { coursesEnabled } = usePlatformFlags()
	const navigate = useNavigate()

	const { data, isLoading, isFetching } = useGetFeaturedStandaloneCoursesQuery(undefined, { skip: !coursesEnabled })
	const loading = isLoading || isFetching
	/* Admin's featured_order — 1 first. Copied before sorting: RTK Query freezes
	   cached arrays, so an in-place sort throws in development. A course with no
	   order sorts last rather than jumping to the front. */
	const courses = React.useMemo(() => {
		const rows = data?.data ?? []
		return [...rows].sort((a, b) => (a.featured_order ?? Number.MAX_SAFE_INTEGER) - (b.featured_order ?? Number.MAX_SAFE_INTEGER))
	}, [data?.data])

	if (!coursesEnabled) return null
	if (!loading && courses.length === 0) return null

	return (
		<FeaturedRail
			title={t('featured_title', { defaultValue: 'كورسات مختارة' })}
			seeAllLabel={t('see_all', { defaultValue: 'عرض الكل' })}
			onSeeAll={() => navigate('/search?tab=courses')}
			loading={loading}
		>
			{courses.map(course => (
				<StandaloneCourseCard key={`featured-${course.id}`} course={course} />
			))}
		</FeaturedRail>
	)
}

export default FeaturedCoursesCarousel
